import {Parameters} from 'platform/components';
import {Align, Display, HStack, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {VehicleLogo} from '../../../components/VehicleLogo/VehicleLogo';
import {useCatalogue} from '../../../hooks/useCatalogue';
import {useVehicleTranslatedInfo} from '../../../hooks/useVehicleTranslatedInfo';
import i18n from '../../../i18n/i18n';
import {useCreatePageContext} from '../components/CreatePageContext';
import {VEHICLETYPE_PASSENGER_CAR} from '../constants/vehiclePassengerCar';

export function VehicleHeader(props: TestIdProps) {
  const {values: vehicle} = useCreatePageContext();
  const catalogue = useCatalogue();

  const formatNumber = useFormatNumber();

  const {data} = useVehicleTranslatedInfo({
    make: vehicle.make,
    modelFamily: vehicle.modelFamily,
    vehicleType: VEHICLETYPE_PASSENGER_CAR,
    trimLevel: vehicle.trimLevel,
  });
  const vehicleName = match([data.make, data.model])
    .with([Pattern.nonNullable, Pattern.nullish], () => data.make)
    .with([Pattern.nonNullable, Pattern.nonNullable], () => `${data.make} ${data.model}`)
    .otherwise(() => undefined);

  const parameters: Array<string | Nullish> = [
    vehicle.firstRegistrationOnYear?.toString(),
    isNotNil(vehicle.mileage)
      ? `${formatNumber(vehicle.mileage)} ${i18n.t('general.metric.km')}`
      : null,
    isNotNil(vehicle.maxPower ?? vehicle.power)
      ? `${vehicle.maxPower ?? vehicle.power} ${i18n.t('general.metric.kW')}`
      : null,
    catalogue.getTransmission(vehicle.transmission?.[0]),
    catalogue.getFuelType(vehicle.fuelType?.[0]),
    catalogue.getDrive(vehicle.drive?.[0]),
    catalogue.getCarStyle(vehicle.bodyStyle?.[0]),
  ].filter(isNotNilOrEmpty);

  return (
    <VStack align="center" spacing={4}>
      <HStack wrap spacing={4} align="center" justify="center">
        <VehicleLogo
          vehicle={{
            make: vehicle.make,
            modelFamily: vehicle.modelFamily,
            vehicleType: VEHICLETYPE_PASSENGER_CAR,
          }}
          height={14}
          width={14}
          color="white"
          data-testid={suffixTestId('vehicleLogo', props)}
        />
        <Align center>
          <Display size={1} color="white" data-testid={suffixTestId('vehicleHeading', props)}>
            {vehicleName}
          </Display>
        </Align>
      </HStack>
      <Align center>
        <Parameters
          parameters={parameters}
          color="white"
          size="base"
          justify="center"
          data-testid={suffixTestId('vahicleParameters', props)}
        />
      </Align>
    </VStack>
  );
}
